import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { changeGuestPassword, fetchGuestMe, updateGuestMe, uploadGuestAvatar } from '../../api/guestApi';
import { useToast } from '../../components/ToastContext';

const toForm = (data) => ({
  name: data?.name || '',
  email: data?.email || '',
  phone: data?.phone || '',
  address: data?.address || '',
  city: data?.city || '',
  country: data?.country || '',
  avatarUrl: data?.avatarUrl || '',
});

const GuestProfile = () => {
  const { profile, setProfile, refreshProfile } = useOutletContext();
  const toast = useToast();
  const [form, setForm] = useState(toForm(profile));
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarUrlSaving, setAvatarUrlSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile) {
      setForm(toForm(profile));
    }
  }, [profile]);

  useEffect(() => {
    if (profile) return;
    const load = async () => {
      try {
        const me = await fetchGuestMe();
        setProfile?.(me);
        setForm(toForm(me));
      } catch (err) {
        setError(err.message || 'Unable to load profile');
      }
    };
    load();
  }, [profile, setProfile]);

  useEffect(() => {
    if (!avatarPreview) return undefined;
    return () => URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  const avatarSrc = useMemo(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    if (avatarPreview) return avatarPreview;
    if (!form.avatarUrl) return '';
    if (form.avatarUrl.startsWith('http')) return form.avatarUrl;
    return `${apiBase}${form.avatarUrl.startsWith('/') ? form.avatarUrl : `/${form.avatarUrl}`}`;
  }, [avatarPreview, form.avatarUrl]);

  const validatePhone = (value) => {
    if (!value) return true;
    const clean = value.replace(/\s+/g, '');
    return /^\+?\d{7,15}$/.test(clean);
  };

  const validateAvatarUrl = (value) => {
    if (!value) return true;
    return /^https?:\/\//i.test(value) || value.startsWith('/');
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = String(form.name || '').trim();
    if (!trimmedName) {
      setError('Full name is required.');
      return;
    }
    if (!validatePhone(form.phone)) {
      setError('Please enter a valid phone number.');
      return;
    }
    if (!validateAvatarUrl(form.avatarUrl)) {
      setError('Avatar URL must start with http(s):// or /.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: trimmedName,
        phone: form.phone,
        address: form.address,
        city: form.city,
        country: form.country,
        avatar_url: form.avatarUrl,
      };
      const updated = await updateGuestMe(payload);
      setProfile?.(updated);
      setForm(toForm(updated));
      toast.success('Profile updated');
    } catch (err) {
      setError(err.message || 'Profile update failed');
      toast.error(err.message || 'Profile update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      setError('Please choose an avatar image first.');
      return;
    }
    if (!avatarFile.type.startsWith('image/')) {
      setError('Avatar must be an image file.');
      return;
    }
    if (avatarFile.size > 5 * 1024 * 1024) {
      setError('Avatar must be smaller than 5MB.');
      return;
    }
    setError('');
    setAvatarSaving(true);
    try {
      const res = await uploadGuestAvatar(avatarFile);
      if (res?.guest) {
        setProfile?.(res.guest);
        setForm(toForm(res.guest));
      } else {
        await refreshProfile?.();
      }
      setAvatarFile(null);
      setAvatarPreview('');
      toast.success('Avatar updated');
    } catch (err) {
      setError(err.message || 'Avatar upload failed');
      toast.error(err.message || 'Avatar upload failed');
    } finally {
      setAvatarSaving(false);
    }
  };

  const saveAvatarUrl = async () => {
    const nextUrl = String(form.avatarUrl || '').trim();
    if (!nextUrl) {
      setError('Please enter an avatar URL.');
      return;
    }
    if (!validateAvatarUrl(nextUrl)) {
      setError('Avatar URL must start with http(s):// or /.');
      return;
    }
    setError('');
    setAvatarUrlSaving(true);
    try {
      const updated = await updateGuestMe({ avatar_url: nextUrl });
      setProfile?.(updated);
      setForm(toForm(updated));
      setAvatarFile(null);
      setAvatarPreview('');
      toast.success('Avatar URL saved');
    } catch (err) {
      setError(err.message || 'Avatar URL update failed');
      toast.error(err.message || 'Avatar URL update failed');
    } finally {
      setAvatarUrlSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordForm.current || !passwordForm.next) {
      setError('Please fill in all password fields.');
      return;
    }
    if (passwordForm.next.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (passwordForm.current === passwordForm.next) {
      setError('New password must be different from current password.');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setError('New password and confirm password do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await changeGuestPassword({ currentPassword: passwordForm.current, newPassword: passwordForm.next });
      setPasswordForm({ current: '', next: '', confirm: '' });
      toast.success('Password updated');
    } catch (err) {
      setError(err.message || 'Password update failed');
      toast.error(err.message || 'Password update failed');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <form onSubmit={saveProfile} className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-600">Personal info</p>
                <h2 className="text-lg font-semibold text-slate-900">Guest details</h2>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Full name</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input
                  type="email"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500"
                  value={form.email}
                  readOnly
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 9000000000"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wide text-emerald-600">Address</p>
              <h2 className="text-lg font-semibold text-slate-900">Stay preferences</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Address line</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Apartment, street, landmark"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">City</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.city}
                  onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Country</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.country}
                  onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </form>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wide text-emerald-600">Avatar</p>
              <h2 className="text-lg font-semibold text-slate-900">Profile image</h2>
            </div>
            <div className="flex flex-col items-center gap-4 text-center">
              {avatarSrc ? (
                <img src={avatarSrc} alt="Guest avatar" className="h-24 w-24 rounded-full object-cover ring-4 ring-emerald-50" />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xl font-semibold">
                  {form.name?.slice(0, 2)?.toUpperCase() || 'GV'}
                </div>
              )}
              <div className="w-full">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                      setAvatarFile(file);
                      setAvatarPreview(URL.createObjectURL(file));
                    }
                  }}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-emerald-700 hover:file:bg-emerald-100"
                />
              </div>
              <div className="w-full text-left">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Avatar URL</label>
                <input
                  type="url"
                  value={form.avatarUrl}
                  onChange={(e) => setForm((prev) => ({ ...prev, avatarUrl: e.target.value }))}
                  placeholder="https://example.com/avatar.png"
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleAvatarUpload}
                disabled={avatarSaving}
                className="inline-flex w-full items-center justify-center rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
              >
                {avatarSaving ? 'Uploading...' : 'Upload avatar'}
              </button>
              <button
                type="button"
                onClick={saveAvatarUrl}
                disabled={avatarUrlSaving}
                className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {avatarUrlSaving ? 'Saving...' : 'Save avatar URL'}
              </button>
            </div>
          </div>

          <form onSubmit={changePassword} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wide text-emerald-600">Security</p>
              <h2 className="text-lg font-semibold text-slate-900">Change password</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-600">Current password</label>
                <input
                  type="password"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">New password</label>
                <input
                  type="password"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={passwordForm.next}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, next: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Confirm new password</label>
                <input
                  type="password"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                />
              </div>
              <button
                type="submit"
                disabled={passwordSaving}
                className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {passwordSaving ? 'Updating...' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GuestProfile;

